import { FC } from 'react';
import Select, {
  createFilter,
  FormatOptionLabelMeta,
  StylesConfig,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';

export type SelectOption = {
  value: string;
  label: string;
  /**
   * Shown under (in the menu) or beside (in the control) the label. Used for
   * the raw model id, so an admin sees both the friendly name and the string
   * the code actually sends to the provider.
   */
  description?: string;
};

interface SearchableSelectProps {
  label?: string;
  value?: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Accept a value that is not in `options`. Used for model ids: providers ship
   * and rename models between our deploys, so an admin has to be able to type
   * one in rather than wait for a code change.
   */
  creatable?: boolean;
  hint?: string;
}

// Matched to the height and radius of the plain inputs elsewhere in the admin.
const styles: StylesConfig<SelectOption, false> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '40px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#333' : 'rgba(0,0,0,.2)',
    boxShadow: 'none',
    backgroundColor: state.isDisabled ? 'rgba(0,0,0,.04)' : '#fff',
    ':hover': { borderColor: state.isFocused ? '#333' : 'rgba(0,0,0,.35)' },
  }),
  menu: (provided) => ({ ...provided, zIndex: 20 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#333'
      : state.isFocused
      ? 'rgba(0,0,0,.06)'
      : '#fff',
    color: state.isSelected ? '#fff' : '#333',
  }),
};

// Typing a model id has to find it even when the option is listed under its
// marketing name, so search covers the value and description too.
const filterOption = createFilter<SelectOption>({
  stringify: (option) =>
    `${option.label} ${option.value} ${option.data.description || ''}`,
});

const formatOptionLabel = (
  option: SelectOption,
  { context }: FormatOptionLabelMeta<SelectOption>
) => {
  // Nothing to add for provider options or for a model an admin typed in,
  // where the label already is the raw id.
  if (!option.description || option.description === option.label) {
    return option.label;
  }

  // The control is one row tall, so the id goes beside the name there and on
  // its own line only in the menu.
  return context === 'value' ? (
    <span>
      {option.label}
      <span className="ml-2 text-[12px] opacity-60">{option.description}</span>
    </span>
  ) : (
    <span className="block leading-tight">
      <span className="block">{option.label}</span>
      <span className="block mt-0.5 text-[12px] opacity-60">
        {option.description}
      </span>
    </span>
  );
};

const SearchableSelect: FC<SearchableSelectProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder = 'Search...',
  disabled = false,
  creatable = false,
  hint,
}) => {
  // A saved value that is not in the catalog (an admin typed it, or the catalog
  // moved on) still has to render as the current selection rather than blank.
  const selected =
    options.find((option) => option.value === value) ||
    (value ? { value, label: value } : null);

  const Component = creatable ? CreatableSelect : Select;

  return (
    <div>
      {label && (
        <label className="block mb-1.5 text-[13px] font-medium text-[rgba(0,0,0,.65)]">
          {label}
        </label>
      )}
      <Component
        options={options}
        value={selected}
        onChange={(option) => onChange((option as SelectOption)?.value || '')}
        placeholder={placeholder}
        isDisabled={disabled}
        isSearchable
        styles={styles}
        menuPlacement="auto"
        filterOption={filterOption}
        formatOptionLabel={formatOptionLabel}
        formatCreateLabel={(input: string) => `Use "${input}"`}
      />
      {hint && (
        <p className="mt-1 text-[12px] text-[rgba(0,0,0,.45)]">{hint}</p>
      )}
    </div>
  );
};

export default SearchableSelect;
